import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { USER_ROLE_PARTNER, USER_ROLE_ADMIN, USER_ROLE_LANDLORD } from 'common/userRoles';
import { PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD } from 'common/propertyAttributeConstants';

import { DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS } from 'common/dataTableConstants';

import { SCREEN_ID_PARTNER_MARKET_LISTING_FLOW } from 'client/constants/screenIds';

import {
  MARKETING_PARTNER_LISTING_UPDATE_STATUS_WITHDRAW_FROM_MARKET,
  MARKETING_PARTNER_LISTING_UPDATE_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_UPDATE_STATUS_MARK_AS_RESOLVED,
} from 'common/marketingPartnerListingConstants';

import branch from 'client/core/branch';
import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
  actionDataTableLoadItemsIfNotFullyLoaded,
} from 'client/helpers/dataTables';

import PropertyAttributeActions from 'client/actions/PropertyAttribute';
import { getPropertyAttributeListKeyId } from 'client/actions/PropertyAttribute/getList';
import PartnerActions from 'client/actions/Partner';

import SrPartnerBringBackOnMarketForm from 'client/components/SrPartnerBringBackOnMarketForm/SrPartnerBringBackOnMarketForm';
import SrPartnerWithdrawFromMarketForm from 'client/components/SrPartnerWithdrawFromMarketForm/SrPartnerWithdrawFromMarketForm';
import SrPartnerTemporarilyOffMarketForm from 'client/components/SrPartnerTemporarilyOffMarketForm/SrPartnerTemporarilyOffMarketForm';
import SrPartnerMarkAsResolvedForm from 'client/components/SrPartnerMarkAsResolvedForm/SrPartnerMarkAsResolvedForm';

function getMarketingListingDataTableSettings({ match }) {
  const { marketingPartnerListingId, partnerSlug } = match.params;

  if (!marketingPartnerListingId) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingPartnerListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `marketing-partner-listing-id-${marketingPartnerListingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': partnerSlug,
      'marketingPartnerListings.id': parseInt(marketingPartnerListingId, 10),
    },
    sort: {
      'marketingPartnerListings.createdDate': 'desc',
    },
  };
  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_MARKET_LISTING_FLOW)
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER, USER_ROLE_LANDLORD],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.propertyAttributes = [
      'propertyAttributes',
      'listBy',
      getPropertyAttributeListKeyId({
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      }),
    ];
  }

  return cursors;
})
@dataTable(getMarketingListingDataTableSettings)
class MarketingPartnerListingStatus extends Component {
  static fetchData(match, inject) {
    const partnersBySlug = inject.state.get(['partners', 'bySlug']);
    const promises = [];

    if (!partnersBySlug[match.params.partnerSlug]) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    if (match.params.marketingPartnerListingId) {
      const dataTableSettings = getMarketingListingDataTableSettings({ match });
      actionInitializeDataTableState(inject, dataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
    }

    return Promise.all(promises).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      return PropertyAttributeActions.getList(inject, {
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      });
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    propertyAttributes: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
  };

  _onPartnerMarketListingFormBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  _onPartnerMarketListingFormSubmit = () => {
    const { history } = this.props;
    this._reloadPartnerListing();
    history.goBack();
  };

  _reloadPartnerListing = () => {
    const { match, dispatch } = this.props;
    return dispatch(actionDataTableLoadItems, getMarketingListingDataTableSettings({ match }));
  };

  render() {
    const { partner, propertyAttributes, dispatch, match, marketingPartnerListing } = this.props;

    if (!partner || (match.params.marketingPartnerListingId && !marketingPartnerListing)) {
      return <div className="loading-gif" />;
    }

    if (match.params.listingStatus === MARKETING_PARTNER_LISTING_UPDATE_STATUS_MARK_AS_RESOLVED) {
      return (
        <SrPartnerMarkAsResolvedForm
          partner={partner}
          propertyAttributes={propertyAttributes}
          marketingPartnerListing={marketingPartnerListing}
          onBack={this._onPartnerMarketListingFormBack}
          onSubmit={this._onPartnerMarketListingFormSubmit}
          dispatch={dispatch}
        />
      );
    }

    if (
      match.params.listingStatus === MARKETING_PARTNER_LISTING_UPDATE_STATUS_TEMPORARILY_OFF_MARKET
    ) {
      return (
        <SrPartnerTemporarilyOffMarketForm
          partner={partner}
          propertyAttributes={propertyAttributes}
          marketingPartnerListing={marketingPartnerListing}
          onBack={this._onPartnerMarketListingFormBack}
          onSubmit={this._onPartnerMarketListingFormSubmit}
          dispatch={dispatch}
        />
      );
    }

    if (
      match.params.listingStatus === MARKETING_PARTNER_LISTING_UPDATE_STATUS_WITHDRAW_FROM_MARKET
    ) {
      return (
        <SrPartnerWithdrawFromMarketForm
          partner={partner}
          propertyAttributes={propertyAttributes}
          marketingPartnerListing={marketingPartnerListing}
          onBack={this._onPartnerMarketListingFormBack}
          onSubmit={this._onPartnerMarketListingFormSubmit}
          dispatch={dispatch}
        />
      );
    }

    return (
      <SrPartnerBringBackOnMarketForm
        partner={partner}
        propertyAttributes={propertyAttributes}
        marketingPartnerListing={marketingPartnerListing}
        onBack={this._onPartnerMarketListingFormBack}
        onSubmit={this._onPartnerMarketListingFormSubmit}
        dispatch={dispatch}
      />
    );
  }
}

MarketingPartnerListingStatus.displayName = 'MarketingPartnerListingStatus';

export default MarketingPartnerListingStatus;
